/**
 * Staff placements (runtime-handoff-v5, directive §13).
 *
 * staff_placements.json ships { name, role, position:[x,y,z], yaw } per person.
 * Staff use SHARED geometry + materials (one instanced draw per role), never one
 * GLB load per person. This module parses + validates them (no staff inside the
 * playing area; no floating staff) and exposes the transform the same way the
 * crowd does — position + Y-yaw, applied once, no offsets.
 */
import { Matrix4 } from 'three';
import { composeCrowdMatrix, type CrowdInstance } from './crowdPlacements.js';

export interface StaffInstance {
  name: string;
  role: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
}

/** Half-extents of the field of play (105×68) — staff must sit outside it. */
const PITCH_HALF_LENGTH = 52.5;
const PITCH_HALF_WIDTH = 34;

export interface StaffParseResult {
  staff: StaffInstance[];
  roles: string[];
  /** Names of any staff found inside the field of play (should be empty). */
  insidePlayingArea: string[];
}

export function parseStaffPlacements(json: unknown): StaffParseResult {
  const root = json as { staff?: Array<{ name: string; role: string; position: number[]; yaw: number }> };
  const staff: StaffInstance[] = [];
  const insidePlayingArea: string[] = [];
  for (const s of root.staff ?? []) {
    const p = s.position ?? [0, 0, 0];
    const inst: StaffInstance = { name: s.name, role: s.role, x: p[0]!, y: p[1]!, z: p[2]!, yaw: s.yaw };
    if (![inst.x, inst.y, inst.z, inst.yaw].every(Number.isFinite)) {
      throw new Error(`staff: "${s.name}" has a non-finite transform`);
    }
    // Inside the field of play (a small tolerance in from the touchline/goal line).
    if (Math.abs(inst.x) < PITCH_HALF_LENGTH - 0.5 && Math.abs(inst.z) < PITCH_HALF_WIDTH - 0.5) {
      insidePlayingArea.push(s.name);
    }
    staff.push(inst);
  }
  return { staff, roles: [...new Set(staff.map((s) => s.role))], insidePlayingArea };
}

/** The one world matrix for a staff member — same contract as the crowd. */
export function composeStaffMatrix(s: StaffInstance, out: Matrix4): Matrix4 {
  const inst: CrowdInstance = { x: s.x, y: s.y, z: s.z, yaw: s.yaw };
  return composeCrowdMatrix(inst, out);
}
